import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { useEffect } from 'react';
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { Ajax } from '_utils';
import { useUser } from './UserProvider';
export const SpaContext = React.createContext({});
SpaContext.displayName = 'SpaContext';
export function useSpaContext() {
    return React.useContext(SpaContext);
}
export function SpaContextProvider(props) {
    const user = useUser();
    const canConnect = Boolean(user && !user.isPublic);
    const [state, setState] = React.useState({});
    const connectionRef = React.useRef(null);
    const eventSourceRef = React.useRef(null);
    const refreshIntervalRef = React.useRef(null);
    const reconnectTimeoutRef = React.useRef(null);
    const sseReconnectTimeoutRef = React.useRef(null);
    const refreshInProgressRef = React.useRef(false);
    const isStoppingRef = React.useRef(false);
    useEffect(() => {
        isStoppingRef.current = false;
        void refreshCompleteState();
        void startConnection();
        startSseConnection();
        const handleBrowserResume = () => {
            if (document.hidden) {
                return;
            }
            void startConnection();
            startSseConnection();
            void refreshCompleteState();
        };
        const handleOnline = () => {
            void startConnection();
            startSseConnection();
            void refreshCompleteState();
        };
        window.addEventListener('focus', handleBrowserResume);
        window.addEventListener('online', handleOnline);
        document.addEventListener('visibilitychange', handleBrowserResume);
        return () => {
            window.removeEventListener('focus', handleBrowserResume);
            window.removeEventListener('online', handleOnline);
            document.removeEventListener('visibilitychange', handleBrowserResume);
            endSseConnection();
            void endConnection();
        };
    }, [canConnect]);
    function handleStateChanged(updatedState) {
        try {
            setState((prevState) => setContextProperty(prevState, updatedState, true));
        }
        catch (e) {
            console.error(e);
        }
    }
    async function startConnection() {
        await SpaContextConnectionSupport.startConnection({
            canConnect,
            connectionRef,
            refreshIntervalRef,
            reconnectTimeoutRef,
            isStoppingRef,
            onStateChanged: handleStateChanged,
            onRefreshNeeded: refreshCompleteState,
            startConnection,
            refreshCompleteState,
        });
    }
    function startSseConnection() {
        SpaContextConnectionSupport.startSseConnection({
            canConnect,
            eventSourceRef,
            sseReconnectTimeoutRef,
            isStoppingRef,
            onStateChanged: handleStateChanged,
            onRefreshNeeded: refreshCompleteState,
            startSseConnection,
        });
    }
    async function endConnection() {
        await SpaContextConnectionSupport.endConnection({
            connectionRef,
            refreshIntervalRef,
            reconnectTimeoutRef,
            isStoppingRef,
        });
    }
    function endSseConnection() {
        SpaContextConnectionSupport.endSseConnection({
            eventSourceRef,
            sseReconnectTimeoutRef,
            isStoppingRef,
        });
    }
    async function refreshCompleteState() {
        await SpaContextConnectionSupport.refreshCompleteState({
            refreshInProgressRef,
            setState,
        });
    }
    return _jsx(SpaContext.Provider, { value: state, children: props.children });
}
function setContextProperty(current, newValues, isTopLevel = false) {
    try {
        const result = { ...current };
        // recursively merge the new values into the current context
        for (const key in newValues) {
            const normKey = normalizeKey(key);
            if (isTopLevel && normKey == 'process') {
                result.processes = handleProcessUpdate(newValues[key], current.processes);
                continue;
            }
            if (isTopLevel && (normKey == 'entity' || normKey == 'entities')) {
                result.entities = handleEntityUpdate(newValues[key], current.entities);
                continue;
            }
            const nextValue = newValues[key];
            if (isObjectRecord(nextValue)) {
                const currentValue = isObjectRecord(current[normKey]) ? current[normKey] : {};
                if (!isObjectRecord(current[normKey])) {
                    result[normKey] = {};
                }
                result[normKey] = setContextProperty(currentValue, nextValue);
            }
            else {
                if (current[normKey] !== nextValue) {
                    result[normKey] = nextValue;
                }
            }
        }
        return result;
    }
    catch (e) {
        console.error(e);
        throw e;
    }
}
function handleProcessUpdate(update, current) {
    if (!isObjectRecord(update)) {
        return current;
    }
    const result = current ? { ...current } : {};
    for (const key in update) {
        try {
            const nextValue = update[key];
            const parsed = parseJsonIfString(nextValue);
            if (!isObjectRecord(parsed)) {
                continue;
            }
            const processInfo = parsed;
            if (processInfo) {
                result[processInfo.id] = processInfo;
            }
        }
        catch (e) {
            console.error(`Failed to parse process info for key ${key} with value ${update[key]}`, e);
        }
    }
    return result;
}
function handleEntityUpdate(update, current) {
    if (!isObjectRecord(update)) {
        return current;
    }
    const result = current ? { ...current } : {};
    for (const key in update) {
        try {
            const parsed = parseJsonIfString(update[key]);
            if (!isObjectRecord(parsed)) {
                continue;
            }
            const normalized = normalizeEntityUpdate(parsed, key);
            if (!normalized) {
                continue;
            }
            const existing = result[normalized.id];
            const mergedProperties = {
                ...(existing?.properties || {}),
                ...normalized.properties,
            };
            result[normalized.id] = {
                ...(existing || {}),
                ...normalized,
                properties: mergedProperties,
            };
        }
        catch (e) {
            console.error(`Failed to parse entity info for key ${key}`, e);
        }
    }
    return result;
}
function normalizeEntityUpdate(entityLike, fallbackId) {
    let id = fallbackId;
    if (typeof entityLike.id === 'string') {
        id = entityLike.id;
    }
    else if (typeof entityLike.entityId === 'string') {
        id = entityLike.entityId;
    }
    if (!id) {
        return null;
    }
    const propertiesCandidate = isObjectRecord(entityLike.properties) && !Array.isArray(entityLike.properties)
        ? entityLike.properties
        : entityLike;
    const properties = normalizeEntityProperties(propertiesCandidate);
    const normalized = {
        ...entityLike,
        id,
        properties,
    };
    if (typeof entityLike.name === 'string') {
        normalized.name = entityLike.name;
    }
    return normalized;
}
function normalizeEntityProperties(properties) {
    const normalized = {};
    for (const propertyName in properties) {
        const normalizedPropertyName = normalizeKey(propertyName.trim());
        normalized[normalizedPropertyName] = normalizeEntityProperty(properties[propertyName]);
    }
    return normalized;
}
function normalizeEntityProperty(value) {
    const parsed = parseJsonIfString(value);
    if (!isObjectRecord(parsed)) {
        return {
            formattedValue: parsed == null ? undefined : String(parsed),
            attributes: {},
        };
    }
    let formattedValue;
    if (typeof parsed.formattedValue === 'string') {
        formattedValue = parsed.formattedValue;
    }
    else if (typeof parsed.formatted === 'string') {
        formattedValue = parsed.formatted;
    }
    const attributes = {};
    if (isObjectRecord(parsed.attributes)) {
        for (const key in parsed.attributes) {
            const attrValue = parsed.attributes[key];
            if (attrValue == null) {
                continue;
            }
            attributes[key] = String(attrValue);
        }
    }
    for (const key in parsed) {
        if (key === 'formattedValue' || key === 'formatted' || key === 'attributes') {
            continue;
        }
        const propertyValue = parsed[key];
        if (propertyValue == null) {
            continue;
        }
        attributes[key] = String(propertyValue);
    }
    return {
        formattedValue,
        attributes,
    };
}
function parseJsonIfString(value) {
    if (typeof value !== 'string') {
        return value;
    }
    try {
        return JSON.parse(value);
    }
    catch {
        return value;
    }
}
function mapSsePayloadToSpaUpdate(payload) {
    if (!isObjectRecord(payload)) {
        return null;
    }
    const entityFullCode = getStringField(payload, 'EntityFullCode') || getStringField(payload, 'entityFullCode');
    const propertyName = getStringField(payload, 'Property') || getStringField(payload, 'property');
    if (!entityFullCode || !propertyName) {
        return null;
    }
    const normalizedEntityId = normalizeEntityIdFromFullCode(entityFullCode);
    const normalizedPropertyName = normalizeKey(propertyName.trim());
    const formattedValue = getStringField(payload, 'FormattedValue') || getStringField(payload, 'formattedValue');
    const timestamp = getStringField(payload, 'Timestamp') || getStringField(payload, 'timestamp');
    const propertyAttributesRaw = payload.Attributes ?? payload.attributes;
    const propertyAttributes = {};
    if (isObjectRecord(propertyAttributesRaw)) {
        for (const key in propertyAttributesRaw) {
            const attrValue = propertyAttributesRaw[key];
            if (attrValue == null) {
                continue;
            }
            propertyAttributes[key] = String(attrValue);
        }
    }
    if (timestamp) {
        propertyAttributes.timestamp = timestamp;
    }
    const property = {
        formattedValue,
        attributes: propertyAttributes,
    };
    return {
        entities: {
            [entityFullCode]: {
                id: normalizedEntityId,
                fullCode: entityFullCode,
                properties: {
                    [normalizedPropertyName]: property,
                },
            },
        },
    };
}
function normalizeEntityIdFromFullCode(fullCode) {
    const segments = fullCode
        .split('/')
        .map(segment => segment.trim())
        .filter(segment => segment.length > 0);
    const withoutState = segments.length > 0 && segments[0].toLowerCase() === 'state' ? segments.slice(1) : segments;
    if (withoutState.length === 0) {
        return normalizeKey(fullCode.trim());
    }
    return withoutState.map(segment => normalizeKey(segment.trim())).join('_');
}
function getStringField(source, fieldName) {
    const value = source[fieldName];
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}
function normalizeKey(key) {
    if (!key || key.length === 0) {
        return '_';
    }
    if (key.length <= 3) {
        return key.toLowerCase();
    }
    // make first letter lowercase
    return key[0].toLowerCase() + key.substring(1);
}
function isObjectRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
class SpaContextConnectionSupport {
    static async startConnection(params) {
        const { canConnect, connectionRef, refreshIntervalRef, reconnectTimeoutRef, isStoppingRef, onStateChanged, onRefreshNeeded, startConnection, refreshCompleteState, } = params;
        SpaContextConnectionSupport.ensureRefreshInterval(refreshIntervalRef, refreshCompleteState);
        if (!canConnect) {
            return;
        }
        const connection = SpaContextConnectionSupport.getOrCreateConnection({
            connectionRef,
            reconnectTimeoutRef,
            isStoppingRef,
            onStateChanged,
            onRefreshNeeded,
            startConnection,
        });
        SpaContextConnectionSupport.clearReconnectTimeout(reconnectTimeoutRef);
        if (connection.state === HubConnectionState.Connected ||
            connection.state === HubConnectionState.Connecting ||
            connection.state === HubConnectionState.Reconnecting) {
            return;
        }
        try {
            await connection.start();
            await refreshCompleteState();
        }
        catch (err) {
            console.error(err);
            SpaContextConnectionSupport.scheduleReconnect({
                reconnectTimeoutRef,
                isStoppingRef,
                startConnection,
            });
        }
    }
    static async endConnection(params) {
        const { connectionRef, refreshIntervalRef, reconnectTimeoutRef, isStoppingRef } = params;
        isStoppingRef.current = true;
        SpaContextConnectionSupport.clearReconnectTimeout(reconnectTimeoutRef);
        if (refreshIntervalRef.current) {
            clearInterval(refreshIntervalRef.current);
            refreshIntervalRef.current = null;
        }
        const connection = connectionRef.current;
        connectionRef.current = null;
        if (connection) {
            try {
                await connection.stop();
            }
            catch (err) {
                console.error(err);
            }
        }
    }
    static async refreshCompleteState(params) {
        const { refreshInProgressRef, setState } = params;
        if (refreshInProgressRef.current) {
            return;
        }
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
            return;
        }
        refreshInProgressRef.current = true;
        try {
            const spaContext = await Ajax.get('/api/spa');
            if (!spaContext) {
                return;
            }
            const mappedContext = setContextProperty({}, spaContext, true);
            // Temporary workaround: /api/spa currently does not include SSE-backed entities.
            // Remove this fallback once the API returns entities as part of the canonical payload.
            setState(prevState => ({
                ...mappedContext,
                entities: mappedContext.entities ?? prevState.entities,
            }));
        }
        catch (ex) {
            console.error(ex);
        }
        finally {
            refreshInProgressRef.current = false;
        }
    }
    static ensureRefreshInterval(refreshIntervalRef, refreshCompleteState) {
        if (!refreshIntervalRef.current) {
            refreshIntervalRef.current = setInterval(() => {
                void refreshCompleteState();
            }, 60 * 1000);
        }
    }
    static clearReconnectTimeout(reconnectTimeoutRef) {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
    }
    static scheduleReconnect(params) {
        const { reconnectTimeoutRef, isStoppingRef, startConnection, delayMs = 5000 } = params;
        if (isStoppingRef.current || reconnectTimeoutRef.current) {
            return;
        }
        reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            void startConnection();
        }, delayMs);
    }
    static getOrCreateConnection(params) {
        const { connectionRef, reconnectTimeoutRef, isStoppingRef, onStateChanged, onRefreshNeeded, startConnection, } = params;
        if (!connectionRef.current) {
            const connection = new HubConnectionBuilder()
                .withUrl('/api/messages')
                .withAutomaticReconnect()
                .build();
            connection.on('StateChanged', onStateChanged);
            connection.onreconnected(() => {
                void onRefreshNeeded();
            });
            connection.onclose((err) => {
                if (isStoppingRef.current) {
                    return;
                }
                if (err) {
                    console.error(err);
                }
                SpaContextConnectionSupport.scheduleReconnect({
                    reconnectTimeoutRef,
                    isStoppingRef,
                    startConnection,
                });
            });
            connectionRef.current = connection;
        }
        return connectionRef.current;
    }
    static startSseConnection(params) {
        const { canConnect, eventSourceRef, sseReconnectTimeoutRef, isStoppingRef, onStateChanged, onRefreshNeeded, startSseConnection, } = params;
        if (!canConnect || eventSourceRef.current) {
            return;
        }
        SpaContextConnectionSupport.clearSseReconnectTimeout(sseReconnectTimeoutRef);
        const source = new EventSource('/house_api/state/changes', {
            withCredentials: true,
        });
        source.onmessage = (event) => {
            try {
                const parsedData = JSON.parse(event.data);
                const mapped = mapSsePayloadToSpaUpdate(parsedData);
                if (mapped) {
                    onStateChanged(mapped);
                    return;
                }
                if (isObjectRecord(parsedData)) {
                    onStateChanged(parsedData);
                }
            }
            catch (error) {
                console.error('Failed to parse SSE message', error);
            }
        };
        source.onerror = () => {
            if (isStoppingRef.current) {
                return;
            }
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            SpaContextConnectionSupport.scheduleSseReconnect({
                sseReconnectTimeoutRef,
                isStoppingRef,
                startSseConnection,
            });
        };
        source.onopen = () => {
            void onRefreshNeeded();
        };
        eventSourceRef.current = source;
    }
    static endSseConnection(params) {
        const { eventSourceRef, sseReconnectTimeoutRef, isStoppingRef } = params;
        isStoppingRef.current = true;
        SpaContextConnectionSupport.clearSseReconnectTimeout(sseReconnectTimeoutRef);
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
    }
    static scheduleSseReconnect(params) {
        const { sseReconnectTimeoutRef, isStoppingRef, startSseConnection, delayMs = 5000, } = params;
        if (isStoppingRef.current || sseReconnectTimeoutRef.current) {
            return;
        }
        sseReconnectTimeoutRef.current = setTimeout(() => {
            sseReconnectTimeoutRef.current = null;
            startSseConnection();
        }, delayMs);
    }
    static clearSseReconnectTimeout(sseReconnectTimeoutRef) {
        if (sseReconnectTimeoutRef.current) {
            clearTimeout(sseReconnectTimeoutRef.current);
            sseReconnectTimeoutRef.current = null;
        }
    }
}
